import { useSyncExternalStore } from 'react';

/**
 * Which MODULE the tenant just hit a `MODULE_NOT_ENABLED` wall on, or null.
 *
 * Same shape and the same reason as {@link appOutageStore}: the two parties sit
 * on opposite sides of an `<Outlet/>`. The api client's response interceptor is
 * what sees the 403, while the shell is what renders the panel — and the route
 * that triggered it lives INSIDE a remote, whose internal routes the shell does
 * not own. A shell-level `<RequireModule>` route guard is therefore the wrong
 * altitude; letting the API be the guard is the only place that knows.
 *
 * How a user gets here at all: the rail item and settings tab for a module the
 * workspace lacks are already hidden, so the only ways in are a typed URL, a
 * bookmark, or a deep link someone pasted. Those deserve an explanation rather
 * than a silent redirect — hence a panel that names the module.
 */
export interface ModuleUnavailable {
  /** `workspace.AppModule.code`, e.g. 'timesheet'. */
  module: string;
  /** App code the module belongs to, e.g. 'PROJECTS'. */
  app: string;
}

let current: ModuleUnavailable | null = null;
const listeners = new Set<() => void>();

/** Publish (or clear, with `null`) the module the tenant is not entitled to. */
export function setModuleUnavailable(next: ModuleUnavailable | null): void {
  if (current?.module === next?.module && current?.app === next?.app) return;
  current = next;
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): ModuleUnavailable | null {
  return current;
}

/** The module the tenant is not entitled to, or null. */
export function useModuleUnavailable(): ModuleUnavailable | null {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
